import { css } from 'aphrodite';
import * as React from 'react';
import { SPECIALIST_JUSTIFICATION_MAX } from '../../../../queries/Admin/Account/useUpdateCustomerSuccessSpecialistMutation';
import { WarningIcon, WarningIconType } from '../../../../web/components/svgs/icons/WarningIcon';
import { Button } from '../../../../web/components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../web/components-v2/Dialog';
import { Field, FieldLabel } from '../../../../web/components-v2/Field';
import { TextArea } from '../../../../web/components-v2/TextArea';
import { specialistChangeJustificationDialogStyles as styles } from './specialistChangeJustificationDialogStyles';

interface IProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	companyName?: string;
	previousSpecialistName?: string;
	newSpecialistName?: string;
	isSaving?: boolean;
	error?: string | null;
	onConfirm: (justification: string) => void;
}

export function SpecialistChangeJustificationDialog({
	open,
	onOpenChange,
	companyName,
	previousSpecialistName,
	newSpecialistName,
	isSaving,
	error,
	onConfirm,
}: IProps) {
	const [justification, setJustification] = React.useState('');

	React.useEffect(() => {
		if (open) {
			setJustification('');
		}
	}, [open]);

	const trimmed = justification.trim();
	const isOverMax = justification.length > SPECIALIST_JUSTIFICATION_MAX;
	const canSubmit = trimmed.length > 0 && !isOverMax && !isSaving;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPopup styles={[styles.dialogContent]}>
				<DialogHeader>
					<DialogTitle>Change Customer Success specialist</DialogTitle>
				</DialogHeader>

				<DialogBody styles={[styles.dialogBody]}>
					<div className={css(styles.warningBanner)}>
						<div className={css(styles.warningIconWrap)}>
							<WarningIcon type={WarningIconType.Red} />
						</div>
						<div>
							<div className={css(styles.warningTitle)}>Heads up, this is an audited change</div>
							<div className={css(styles.warningBody)}>
								Changing the specialist on an established account sends a billing audit email to leadership with your
								name, the previous and new specialist, and your justification. Make sure the justification is clear,
								auditors may follow up.
							</div>
						</div>
					</div>

					{!!companyName && (
						<div className={css(styles.contextLine)}>
							Account: <strong>{companyName}</strong>
						</div>
					)}

					<div className={css(styles.fieldGroup)}>
						<div className={css(styles.label)}>Specialist</div>
						<div className={css(styles.changeLine)}>
							{previousSpecialistName || 'Unassigned'} &rarr; <strong>{newSpecialistName || 'Unassigned'}</strong>
						</div>
					</div>

					<Field styles={[styles.fieldGroup]}>
						<FieldLabel htmlFor='account-specialist-justification' styles={[styles.label]}>
							Justification *
						</FieldLabel>
						<TextArea
							id='account-specialist-justification'
							value={justification}
							onChange={e => setJustification(e.target.value)}
							placeholder='Why is this change being made? Auditors may follow up.'
							rows={4}
							maxLength={SPECIALIST_JUSTIFICATION_MAX + 50}
							disabled={isSaving}
						/>
						<div className={css(styles.characterCount, isOverMax && styles.characterCountOver)}>
							{justification.length} / {SPECIALIST_JUSTIFICATION_MAX}
						</div>
					</Field>

					{!!error && <div className={css(styles.errorMessage)}>{error}</div>}
				</DialogBody>

				<DialogFooter>
					<Button variant='secondary' onClick={() => onOpenChange(false)} disabled={isSaving}>
						Cancel
					</Button>
					<Button variant='destructive' onClick={() => onConfirm(trimmed)} disabled={!canSubmit} isLoading={isSaving}>
						Save Changes
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
}
